import { JSX, useEffect, useState } from "react";
import { classNames } from "utils/helper/helper";

/** Up to two initials from a person's or business's name. */
export function initials(name?: string | null): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const letters = words.length === 1 ? words[0].slice(0, 1) : words[0][0] + words[words.length - 1][0];
  return letters.toUpperCase();
}

/**
 * Profile photo, or the name's initials when there is no photo or it fails
 * to load (instead of a stock picture of someone else).
 */
function Avatar({
  src,
  name,
  className = "w-10 h-10 text-sm",
}: {
  src?: string | null;
  name?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name ?? "profile"}
        onError={() => setFailed(true)}
        className={classNames("rounded-full object-cover shrink-0", className)}
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={name ?? "profile"}
      className={classNames(
        "rounded-full shrink-0 inline-flex items-center justify-center bg-[#E5571A] text-white font-semibold",
        className
      )}>
      {initials(name)}
    </span>
  );
}

export default Avatar;

/**
 * Full-width cover photo (provider cards and profile header) that falls back
 * to a large initials avatar on a tinted panel.
 */
export function CoverPhoto({
  src,
  name,
  className,
  children,
}: {
  src?: string | null;
  name?: string | null;
  className: string;
  children: (onError: () => void) => JSX.Element;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) return children(() => setFailed(true));
  return (
    <div
      className={classNames(
        "w-full flex items-center justify-center bg-[#FDEEE7] dark:bg-casagray-700",
        className
      )}>
      <Avatar
        name={name}
        className="w-28 h-28 text-4xl"
      />
    </div>
  );
}
